import { AxiosResponse } from 'axios';
import _ from 'lodash';
import axiosWithToken from 'services/clients/axios-with-token';
import {
  Appointment,
  Container,
  CurrentContainerStatus,
  LegacyContainerStatus,
  PerDiemReportStatus,
  PickupInfo,
  ReturnAppointmentsDictionary,
  Shipment,
} from 'types/Watchlist';
import {
  convertRelativeDate,
  joinArrayParams,
  objectKeysToCamelCase,
  objectKeysToSnakeCase,
} from 'utils/common';

import { INSTRUCTIONS_UNAVAILABLE_ID, OPEN_GATE_STATUS } from 'pages/TrackAndTrace/config';
import { Customer } from 'types/Customer';
import moment from 'moment';
import { CONTAINER_NUMBERS_RE } from 'services/helpers/containers';
import { ShippingLine, Terminal } from 'types/Shipping';
import { UnwatchedState, UnwatchedStateMapping } from 'pages/Shipments/Containers/types';
import {
  ContainerEvent,
  TriggerPickupAppointmentsDictResults,
  TriggerPickupAppointmentDict,
} from '../../types/Watchlist';
import { Port } from './ports';

export enum WatchlistQueryKeys {
  WatchedShipments = 'watchedShipments',
  ShippersListCount = 'shippersListCount',
  Watchlist = 'watchlist',
}

export type JobStatus = 'error' | 'success' | 'pending' | 'duplicate' | 'in_progress' | 'retrying';

export interface WatchListFilterCounts {
  currentNotFound: number;
  currentOnVessel: number;
  currentImport: number;
  currentGatedOut: number;
  currentTerminated: number;
  totalOnVessel: number;
  totalImport: number;
  totalGatedOut: number;
  totalTerminated: number;
  totalWatched: number;
  totalNotFound: number;
  customerCounts: { customerCode: string; count: number }[];
}

export interface DateRangeFilter {
  exactDate: string | null;
  startDate: string | null;
  endDate: string | null;
  includeStartDate: boolean | null;
  includeEndDate: boolean | null;
}

export type BookedAppointmentSatus =
  | 'completed'
  | 'cancelled'
  | 'scheduled'
  | 'other'
  | 'rescheduled'
  | 'missed'
  | 'no_show';

export type ImportSatusValue = 'vessel' | 'yard' | 'departed' | 'rail' | 'unknown';

export interface WatchListFilters {
  availableForPickup?: boolean;
  billOfLadingNumbers?: string[];
  closedArea?: boolean;
  containerNumbers?: string[];
  containerStatus?: Array<LegacyContainerStatus | CurrentContainerStatus>;
  containerType?: string[];
  create?: boolean;
  customers?: string[];
  demurrageDue?: boolean;
  deleted?: boolean;
  gatedInAt?: DateRangeFilter | null;
  gatedOutAt?: DateRangeFilter | null;
  hasPerDiemFees?: boolean;
  holdTypes?: Array<'all' | 'customs' | 'line' | 'tmf' | 'terminal' | 'ctf' | 'freight' | 'others'>;
  importStatus?: Array<ImportSatusValue>;
  lastFreeDayAt?: DateRangeFilter | null;
  onlyShipments?: boolean;
  page?: number;
  pageSize?: number;
  pickupAppointmentAt?: DateRangeFilter;
  pickupAppointmentStatus?: BookedAppointmentSatus[];
  returnAppointmentStatus?: BookedAppointmentSatus[];
  shippingLine?: string[];
  sortBy?: string;
  terminalPickupKeys?: string[];
  unwatched?: boolean | null;
  unwatchedState?: string; // Not used for the query, only for the search params
  watchedAt?: DateRangeFilter | null;
  estPerDiemLfd?: DateRangeFilter | null;
}

export const removeNullAndFormatFilters = (filters: WatchListFilters) => {
  const keys: (keyof Pick<
    WatchListFilters,
    | 'lastFreeDayAt'
    | 'pickupAppointmentAt'
    | 'gatedOutAt'
    | 'gatedInAt'
    | 'watchedAt'
    | 'estPerDiemLfd'
  >)[] = [
    'lastFreeDayAt',
    'pickupAppointmentAt',
    'gatedOutAt',
    'gatedInAt',
    'watchedAt',
    'estPerDiemLfd',
  ];
  const newFilters: WatchListFilters = filters;
  keys.forEach((key) => {
    const value: DateRangeFilter = newFilters[key] as DateRangeFilter;

    if (!_.isNil(value) && Object.values(value || {}).filter((f) => f !== null).length === 0) {
      newFilters[key] = undefined;
    } else if (!_.isNil(value)) {
      const passThrough = ['includeStartDate', 'includeEndDate'];
      Object.entries(value).forEach(([dateKey, v]) => {
        if (v) {
          if (passThrough.includes(dateKey as string)) {
            value[dateKey as keyof DateRangeFilter] = v;
          } else {
            value[dateKey as keyof DateRangeFilter] = convertRelativeDate(v)?.format('YYYY-MM-DD');
          }
        }
      });
      newFilters[key] = value;
    }
  });

  // Convert UnwatchedState to corresponding boolean values or null
  if (filters.unwatchedState) {
    newFilters.unwatched =
      UnwatchedStateMapping[filters.unwatchedState || UnwatchedState.OnlyWatched];
  }
  delete newFilters.unwatchedState; // Only used to set search params, not for the actual query

  return newFilters;
};

interface ShippersListCount {
  count: number;
}

const updateEstPerDiemLfd = (
  estPerDiemLfd: DateRangeFilter | null | undefined
): DateRangeFilter => {
  // TypeScript doesn't like the spread operator on DateRangeFilter
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return {
    ...estPerDiemLfd,
    startDate: estPerDiemLfd?.startDate
      ? convertRelativeDate(estPerDiemLfd.startDate).format('YYYY-MM-DD')
      : null,
    endDate: estPerDiemLfd?.endDate
      ? convertRelativeDate(estPerDiemLfd.endDate).format('YYYY-MM-DD')
      : null,
  };
};

export const getShippersListCount = async ({
  filters,
  signal = undefined,
}: {
  filters: WatchListFilters;
  signal?: AbortSignal;
}): Promise<ShippersListCount> => {
  let modifiedFilters: WatchListFilters = { ...filters, onlyShipments: true };
  if (filters.estPerDiemLfd) {
    modifiedFilters = {
      ...modifiedFilters,
      estPerDiemLfd: updateEstPerDiemLfd(modifiedFilters.estPerDiemLfd),
    };
  }

  const res = await axiosWithToken.post(
    '/core/api/v1/containers/watch-list/count',
    objectKeysToSnakeCase(modifiedFilters, true),
    {
      signal,
    }
  );
  const { data } = res || {};
  return objectKeysToCamelCase(data);
};

export const getWatchListCount = async ({
  filters,
  signal = undefined,
}: {
  filters: WatchListFilters;
  signal?: AbortSignal;
}): Promise<WatchListFilterCounts> => {
  const res = await axiosWithToken.post(
    '/core/api/v1/containers/watch-list/deprecated/count',
    objectKeysToSnakeCase(removeNullAndFormatFilters(filters), true),
    {
      signal,
    }
  );
  const { data } = res || {};
  return objectKeysToCamelCase(data);
};

export interface WatchList {
  count: number;
  next: string | null;
  noCapacityContainers: string[];
  results: Container[];
  missingWatch?: string[];
}

export async function fetchWatchList({
  filters,
  signal = undefined,
}: {
  filters: WatchListFilters;
  signal: AbortSignal | undefined;
}) {
  let { containerNumbers = [] } = filters;
  // handles default sorting
  let sortBy = 'default';
  if (filters.sortBy) {
    sortBy = filters.sortBy;
  } else if (containerNumbers.length > 0) {
    sortBy = 'container_numbers_input';
  }

  if (containerNumbers.length > 0) {
    containerNumbers = containerNumbers.filter((c) => CONTAINER_NUMBERS_RE.test(c));
    // If all the container numbers are invalid, then we return empty results
    if (containerNumbers.length === 0) {
      return {
        count: 0,
        next: null,
        noCapacityContainers: [],
        results: [],
      } as WatchList;
    }
  }

  const response: AxiosResponse = await axiosWithToken.post(
    '/core/api/v1/containers/watch-list',
    objectKeysToSnakeCase(
      { ...removeNullAndFormatFilters({ ...filters, containerNumbers }), sortBy },
      true
    ),
    {
      params: { page: filters.page, pageSize: filters.pageSize },
      signal,
    }
  );

  const watchList: WatchList = objectKeysToCamelCase(response.data);

  return watchList;
}

interface RemoteAppointment {
  pickup_booked_appointment: null | unknown;
  available_pickup_appointment: null | unknown;
  container_number: string;
  last_refreshed_pickup_appointments: string;
  last_triggered_pickup_appointments: string;
  return_booked_appointment: null | unknown;
}

interface AppointmentsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: RemoteAppointment[];
}

export async function fetchPickupAppointments({
  containers,
  pageSize,
  signal,
}: {
  containers: Container[];
  pageSize: number;
  signal?: AbortSignal;
}) {
  const payload = { containerNumbers: containers.map((c) => c.containerNumber) };
  const response: AxiosResponse<AppointmentsResponse> = await axiosWithToken.post(
    '/core/api/v1/tas/container-appointments',
    objectKeysToSnakeCase(payload),
    {
      params: { page_size: pageSize },
      signal,
    }
  );

  const appointments: Appointment[] = objectKeysToCamelCase(response.data.results);
  return appointments;
}

export async function fetchTriggerPickupAppointments({
  containers,
  pageSize,
  signal,
}: {
  containers: Container[];
  pageSize: number;
  signal?: AbortSignal;
}) {
  const payload = { containerNumbers: containers.map((c) => c.containerNumber) };
  const response: AxiosResponse<TriggerPickupAppointmentsDictResults> = await axiosWithToken.post(
    '/core/api/v1/containers/trigger-scraping/',
    objectKeysToSnakeCase(payload),
    {
      params: { page_size: pageSize },
      signal,
    }
  );

  const appointments: TriggerPickupAppointmentDict = objectKeysToCamelCase(response.data);
  return appointments;
}

export async function fetchPerDiemReportStatuses({
  containers,
  pageSize,
  signal,
}: {
  containers: Container[];
  pageSize: number;
  signal?: AbortSignal;
}) {
  const params = {
    containerNumbers: containers.map((c) => c.containerNumber),
    pageSize,
  };

  const response = await axiosWithToken.get('/core/api/v1/erl/per_diem_report_range_status', {
    params: _.mapValues(objectKeysToSnakeCase(params), joinArrayParams),
    signal,
  });

  const statuses: PerDiemReportStatus[] = objectKeysToCamelCase(response.data.results);
  return statuses;
}

export interface ExportWatchListResponse {
  id: string;
  status: JobStatus;
  message?: string;
  fileLink?: string;
}

export async function getExportStatus(id: string | null): Promise<WatchListUpload> {
  const response = await axiosWithToken.get<string>(
    `/core/api/v1/containers/watch-list/export/${id}`
  );
  const data = objectKeysToCamelCase(response.data);
  return data;
}

export async function exportWatchList({
  filters,
  selectedColumns,
}: {
  filters: WatchListFilters;
  selectedColumns?: string[];
}): Promise<ExportWatchListResponse> {
  const { containerNumbers = [] } = filters;
  const sortBy = containerNumbers.length > 0 ? 'container_numbers_input' : 'per_diem';
  const response = await axiosWithToken.post(
    '/core/api/v1/containers/watch-list/export',
    objectKeysToSnakeCase(
      { ...removeNullAndFormatFilters({ ...filters, sortBy }), selectedColumns },
      true
    )
  );

  const data: ExportWatchListResponse = objectKeysToCamelCase(response.data);
  return data;
}

interface WatchListAddResponse {
  no_capacity_container: string[];
  container_numbers: string[];
}

interface WatchListAddAlreadyExists {
  code: 'alreadyExists';
}
interface WatchListAddOk {
  code: 'ok';
  noCapacityContainer: string[];
  containerNumbers: string[];
}

type WatchListAdd = WatchListAddAlreadyExists | WatchListAddOk;

interface WatchListUpload {
  status: JobStatus;
  containerTransactionWatchImportId: string;
  firstRows: string[][];
  numberOfColumnsFound: number;
  customerColumn: null | number;
  containerNumberColumn: null | number;
  hasHeader: boolean;

  message?: string;
}

export async function postWatchListAdd(
  containerNumbers: string[],
  customerId: string | null = null
): Promise<WatchListAdd> {
  const response = await axiosWithToken.post<WatchListAddResponse>(
    '/core/api/v1/containers/watch-list/add',
    objectKeysToSnakeCase({ containerNumbers, customerId })
  );

  if (response.status === 201) {
    return { code: 'alreadyExists' };
  }

  const data: WatchListAdd = { code: 'ok', ...objectKeysToCamelCase(response.data) };
  return data;
}

interface ShipmentsAdd {
  billOfLadings: { number: string; carrierScac?: string; shippingLine: string }[];
}

interface ShipmentsAddRawResponse {
  noCapacityBillOfLading: string[];
  billOfLadings: string[];
}
interface ShipmentsAddAlreadyExist {
  code: 'alreadyExists';
}

interface ShipmentsAddOk extends ShipmentsAddRawResponse {
  code: 'ok';
}

type ShipmentsAddResponse = ShipmentsAddAlreadyExist | ShipmentsAddOk;

export async function postShipmentsAdd(values: ShipmentsAdd): Promise<ShipmentsAddResponse> {
  const requestData = {
    bill_of_ladings: values.billOfLadings.map((o) => objectKeysToSnakeCase(o)),
  };
  const response = await axiosWithToken.post<ShipmentsAddRawResponse>(
    '/core/api/v1/containers/watch-list/add',
    requestData
  );

  if (response.status === 201) {
    return { code: 'alreadyExists' };
  }

  const data: ShipmentsAddRawResponse = objectKeysToCamelCase(response.data);
  return { code: 'ok', ...data };
}

export async function uploadFileToWatchList(
  file: File,
  customerId: string | null = null,
  uploadType = 'containers'
): Promise<WatchListUpload> {
  const formData = new FormData();
  formData.append('file', file);
  if (customerId) {
    formData.append('customer_id', customerId);
  }
  formData.append('upload_type', uploadType);
  const response = await axiosWithToken.post<string>('/core/api/v1/containers/upload', formData, {
    headers: {
      'Content-Disposition': `attachment; filename=${file.name}`,
      'Content-Type': 'multipart/form-data',
    },
  });
  const data = objectKeysToCamelCase(response.data);
  return data;
}

export async function getUploadStatus(id: string | null): Promise<WatchListUpload> {
  const response = await axiosWithToken.get<string>(`/core/api/v1/containers/upload/${id}`);
  const data = objectKeysToCamelCase(response.data);
  return data;
}

interface WatchListUpdateParams {
  containerNumbers?: string[];
  transactionIds?: string[];
  view?: WatchListFilters;
  updateWatched?: boolean;
  updateDeleted?: boolean;
}

export async function updateWatchListWithDeleted({
  transactionIds,
  view,
  updateDeleted,
}: WatchListUpdateParams): Promise<void> {
  const params = {
    transactionIds,
    view: view ? removeNullAndFormatFilters(view) : null,
    updateDeleted,
  };
  await axiosWithToken.put<WatchListAddResponse>(
    '/core/api/v1/containers/watch-list',
    objectKeysToSnakeCase(params, true)
  );
}

export async function updateWatchList({
  transactionIds,
  view,
  updateDeleted,
}: WatchListUpdateParams): Promise<void> {
  const params = {
    transactionIds,
    view: view ? removeNullAndFormatFilters(view) : null,
    updateDeleted,
  };
  await axiosWithToken.put<WatchListAddResponse>(
    '/core/api/v1/containers/watch-list',
    objectKeysToSnakeCase(params, true)
  );
}

// TODO: legacy; refactor
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function decorateTerminalStatusRow(row: any) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const decoratedRow = Object.keys(row).reduce((acc: any, key) => {
    acc[_.camelCase(key)] = row[key];
    return acc;
  }, {});

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  decoratedRow.appointments = row.appointments.map((appointment: any) => {
    const decoratedAppointment = {
      ...decoratedRow,
      periodFromDate: appointment.period_from
        ? moment(appointment.period_from).tz(row.timezone)
        : null,
      periodToDate: appointment.period_to ? moment(appointment.period_to).tz(row.timezone) : null,
      slots: appointment.slots,
    };
    delete decoratedAppointment.appointments;
    return decoratedAppointment;
  });
  return decoratedRow;
}

// TODO: legacy; refactor
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function decorateTerminalStatuses(terminalStatusResponse: any) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return Object.keys(terminalStatusResponse).reduce((acc: any, key) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    acc[key] = terminalStatusResponse[key].map((row: any) => decorateTerminalStatusRow(row));
    return acc;
  }, {});
}

// TODO: legacy; refactor
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function appointmentMatch(appointment: any) {
  const currentDateTime = moment();
  const allowedGateSituation =
    appointment.gateStatus === OPEN_GATE_STATUS || appointment.gateStatus === null;
  const timeMatch = currentDateTime.isBefore(appointment.periodToDate);
  return (
    allowedGateSituation &&
    appointment.periodFromDate &&
    timeMatch &&
    appointment.availabilityId &&
    appointment.availabilityId !== INSTRUCTIONS_UNAVAILABLE_ID
  );
}

// TODO: legacy; refactor
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function simplifyAvailableAppointments(terminalStatuses: any) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const appointmentsByLineSize = Object.keys(terminalStatuses).reduce((acc: any, key) => {
    acc[key] = terminalStatuses[key]
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .reduce((accc: any, row: any) => {
        return accc.concat(row.appointments);
      }, [])
      .filter(appointmentMatch);
    return acc;
  }, {});

  return { appointments: appointmentsByLineSize, terminalStatuses };
}

export async function fetchTerminalStatuses({
  containers,
  signal,
}: {
  containers: Container[];
  signal?: AbortSignal;
}): Promise<ReturnAppointmentsDictionary> {
  const relevantContainers = containers.filter((container) => {
    const { shippingLineKey, containerTypeKey, gatedIn } = container;
    return !gatedIn && shippingLineKey && containerTypeKey;
  });
  const lineSizesMap = relevantContainers.reduce(
    (acc: Record<string, [string, string]>, result) => {
      const { shippingLineKey, containerTypeKey } = result;

      acc[`${shippingLineKey}${containerTypeKey}`] = [shippingLineKey, containerTypeKey];
      return acc;
    },
    {}
  );

  const portCollectionCodes = relevantContainers
    .filter((container) => container.gatedOut)
    .reduce((acc: string[], container) => {
      const { portCollectionCode } = container.gatedOut || {};
      if (!portCollectionCode) return acc;
      if (acc.includes(portCollectionCode)) return acc;
      return [...acc, portCollectionCode];
    }, []);

  const { data: terminalStatusRows } = await axiosWithToken.post(
    '/core/api/v1/tas/terminal-status',
    {
      'line-sizes': Object.values(lineSizesMap),
      port_collection_codes: _.uniq(portCollectionCodes),
    },
    { signal }
  );

  return simplifyAvailableAppointments(decorateTerminalStatuses(terminalStatusRows));
}

export type AssignCustomerTarget = {
  containers?: Container[];
  view?: WatchListFilters;
};

export type AssignmentMethod = 'assignment-modal' | 'table-column';

export async function assignCustomer({
  target: { containers, view },
  customer,
  method,
}: {
  target: AssignCustomerTarget;
  customer: Customer | null;
  method: AssignmentMethod;
}) {
  await axiosWithToken.put<WatchListAddResponse>('/core/api/v1/containers/watch-list', {
    transaction_ids: containers ? containers.map((c) => c.transaction) : undefined,
    view: view ? objectKeysToSnakeCase(removeNullAndFormatFilters(view)) : undefined,
    update_customer_id: true,
    customer_id: customer ? customer.id : null,
    customer_id_update_method: method,
  });
}

export async function assignCustomersToUpload(data: {
  containerTransactionWatchImportId: string;
  selectedCustomerId: string | null;
  selectedColumnIndex: number | null;
}) {
  await axiosWithToken.post(
    `/core/api/v1/containers/upload/customers`,
    objectKeysToSnakeCase(data)
  );
}

export interface WatchedShipment {
  createdAt: string;
  createdBy: string | null;
  customer: Customer | null;
  shipment: Shipment;
  trackingStatus: 'TRACKING' | 'LOCATING' | 'UNABLE_TO_LOCATE' | 'ARCHIVED' | 'DELETED';
  containerNumber: string | null;
}

export interface WatchedShipmentResponse {
  count: number;
  next: string;
  previous: string;
  results: WatchedShipment[];
}

export interface WatchedShipmentRequest {
  customers?: (string | null)[];
  trackingStatus?: string[];
  bols?: string[];
  page?: number;
  pageSize?: number;
  containerNumbers?: string[];
}

export async function fetchWatchedShipments(
  signal?: AbortSignal,
  filters?: WatchedShipmentRequest
) {
  const { data } = await axiosWithToken.get<WatchedShipmentResponse>(
    '/core/api/v1/containers/watch-list/watched-shipments',
    {
      params: filters
        ? objectKeysToSnakeCase({
            ...filters,
            customers:
              (filters.customers || []).length > 0
                ? joinArrayParams(
                    filters.customers?.map((c) => (c === null ? 'null' : encodeURIComponent(c)))
                  )
                : undefined,
            container_numbers:
              (filters.containerNumbers || []).length > 0
                ? joinArrayParams(filters.containerNumbers)
                : undefined,
            bols: (filters.bols || []).length > 0 ? joinArrayParams(filters.bols) : undefined,
            trackingStatus:
              (filters.trackingStatus || []).length > 0
                ? joinArrayParams(filters.trackingStatus)
                : undefined,
          })
        : undefined,
      signal,
    }
  );
  return objectKeysToCamelCase(data);
}

export interface ContainerEventChange {
  fieldKey: string;
  fieldCategory: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  oldValue: any; // Set by the backend
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  newValue: any; // Set by the backend
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  leftCol?: any; // Set by transforming/formatting oldValue
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rightCol?: any; // Set by transforming/formatting newValue
  label?: string; // Set by transforming/formatting fieldKey
  tooltip?: string;
}

export interface ContainerHistoryEvent {
  eventType: string;
  eventSubtype: CurrentContainerStatus;
  occurredAt: string;
  source: string;
  terminal: Terminal;
  port: Port;
  timezone: string;
  shippingLine: ShippingLine;
  changesSummary?: string;
  changes: ContainerEventChange[];
  originalEvent: PickupInfo & ContainerEvent;
  milestoneType: string;
}

export async function fetchContainerHistory(transactionId: string) {
  const { data } = await axiosWithToken.get<ContainerHistoryEvent>(
    `/core/api/v1/containers/watch-list/${transactionId}/history`
  );
  return objectKeysToCamelCase(data);
}

export async function triggerPickupScraping(containerNumbers: string[]) {
  const { data } = await axiosWithToken.post('/core/api/v1/containers/trigger-pickup-scraping/', {
    container_numbers: containerNumbers.slice(0, 25), // API handles a max of 25 container numbers
  });
  return objectKeysToCamelCase(data);
}
